<template>
  <div class="service-record-files">
    <div class="service-record-files__title-section">
      <div class="service-record-files__title">Filer</div>
    </div>
    <div v-if="files.length === 0" class="service-record-files__no-files">
      <div class="service-record-files__no-files-icon">
        <i class="pi pi-file" />
      </div>
      <div class="service-record-files__no-files-text">
        <div class="service-record-files__no-files-title">
          Ingen filer tilgjengelig
        </div>
        <div class="service-record-files__no-files-subtitle">
          Det er ingen vedlegg knyttet til denne serviceoppføringen.
        </div>
      </div>
    </div>
    <div v-else class="service-record-files__list">
      <div
        v-for="file in files"
        :key="file.attachmentId"
        class="service-record-files__file"
      >
        <div class="service-record-files__file-actions">
          <SupplierAttachment
            :file-name="file.fileName"
            :attachment-id="file.attachmentId"
            :supplier-id="supplierId"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SupplierAttachment from "@/components/Attachment/SupplierAttachment.vue";
import type { ServiceRecordAttachment } from "@/types/service-records";

interface Props {
  files: ServiceRecordAttachment[];
  supplierId: number;
}

defineProps<Props>();
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.service-record-files {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media only screen and (max-width: $bp-sm) {
    padding: 1rem 1.5rem;
  }

  &__title-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid $color-primary-light;
    padding-bottom: 0.75rem;
  }

  &__title {
    font-size: $font-size-base;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: $color-primary;
    font-weight: 600;
  }

  &__no-files {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    background-color: rgba($color-primary-lighter, 0.3);
    border-radius: 8px;
    border: 2px solid rgba($color-primary, 0.2);
    text-align: center;
    gap: 1rem;
  }

  &__no-files-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: rgba($color-primary, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 1.5rem;
      color: $color-primary;
    }
  }

  &__no-files-text {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__no-files-title {
    font-size: $font-size-base;
    font-weight: 600;
    color: $color-primary-darker;
  }

  &__no-files-subtitle {
    font-size: $font-size-sm;
    color: $color-text-secondary;
    line-height: 1.4;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__file {
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.2s ease;

    @media only screen and (max-width: $bp-sm) {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }
  }

  &__file-name {
    font-weight: $font-weight-medium;
    font-size: $font-size-sm;
    line-height: 1.25;
    color: $color-primary-darker;
    word-break: break-word;
    flex: 1;
    min-width: 0;

    @media only screen and (max-width: $bp-sm) {
      font-size: $font-size-sm;
    }
  }

  &__file-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;

    @media only screen and (max-width: $bp-sm) {
      justify-content: space-between;
    }
  }
}
</style>
